// src/components/clienti/ClienteForm.jsx
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Save, X } from "lucide-react";
import { useState } from "react";

export default function ClienteForm({ cliente, onSubmit, onCancel, isLoading = false, isMobile = false }) {
  const [formData, setFormData] = useState({
    nome: cliente?.nome || "",
    cognome: cliente?.cognome || "",
    telefono: cliente?.telefono || "",
    email: cliente?.email || "",
    pricing_model: cliente?.pricing_model || "no_cost",
    daily_rate: cliente?.daily_rate?.toString() || "",
    monthly_rate: cliente?.monthly_rate?.toString() || ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare data for submission
    const submitData = {
      nome: formData.nome.trim(),
      cognome: formData.cognome.trim(),
      telefono: formData.telefono.trim(),
      email: formData.email.trim(),
      pricing_model: formData.pricing_model,
      daily_rate: formData.pricing_model === 'per_day' ? parseFloat(formData.daily_rate) || 0.00 : 0.00,
      monthly_rate: formData.pricing_model === 'monthly_flat' ? parseFloat(formData.monthly_rate) || 0.00 : 0.00
    };
    
    onSubmit(submitData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePricingModelChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      pricing_model: value,
      // Clear rates when switching models
      daily_rate: value === 'per_day' ? prev.daily_rate : "",
      monthly_rate: value === 'monthly_flat' ? prev.monthly_rate : ""
    }));
  };

  const handleRateChange = (value) => {
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return;
    }
    
    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
    }
    
    const finalValue = parts.join('.');
    
    if (formData.pricing_model === 'per_day') {
      handleChange('daily_rate', finalValue);
    } else if (formData.pricing_model === 'monthly_flat') {
      handleChange('monthly_rate', finalValue);
    }
  };

  // Get current rate value based on pricing model
  const getCurrentRateValue = () => {
    if (formData.pricing_model === 'per_day') return formData.daily_rate;
    if (formData.pricing_model === 'monthly_flat') return formData.monthly_rate;
    return "";
  };

  // Get rate label based on pricing model
  const getRateLabel = () => {
    if (formData.pricing_model === 'per_day') return "Tariffa Giornaliera (€) *";
    if (formData.pricing_model === 'monthly_flat') return "Tariffa Mensile (€) *";
    return "Tariffa";
  };

  // Get rate placeholder based on pricing model
  const getRatePlaceholder = () => {
    if (formData.pricing_model === 'per_day') return "es. 15.00";
    if (formData.pricing_model === 'monthly_flat') return "es. 200.00";
    return "";
  };

  // Check if rate field should be shown
  const shouldShowRateField = formData.pricing_model !== 'no_cost';

  // Native Select Component for mobile
  const NativeSelect = ({ value, onChange, options, placeholder, disabled = false }) => {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full p-2 border border-slate-300 rounded-md text-base bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-500 glass-effect"
        style={{ minHeight: '44px' }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  const pricingOptions = [
    { value: 'no_cost', label: 'Nessun costo' },
    { value: 'per_day', label: 'Giornaliero per auto' },
    { value: 'monthly_flat', label: 'Mensile fisso' }
  ];

  return (
    <Card className="glass-effect border-0 shadow-xl">
      <CardHeader className="p-6 border-b border-slate-100">
        <CardTitle className="text-xl font-bold text-slate-800">
          {cliente ? "Modifica Cliente" : "Nuovo Cliente"}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information Section */}
          <div>
            <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center">
              <span>Informazioni Personali</span>
            </h3>
            <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>
              <div className="space-y-2">
                <Label htmlFor="nome" className="text-sm font-medium text-slate-700">
                  Nome *
                </Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => handleChange("nome", e.target.value)}
                  placeholder="Nome"
                  required
                  disabled={isLoading}
                  className={`glass-effect border-0 shadow-sm ${isMobile ? 'text-base' : ''}`}
                  style={isMobile ? { minHeight: '44px' } : {}}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cognome" className="text-sm font-medium text-slate-700">
                  Cognome *
                </Label>
                <Input
                  id="cognome"
                  value={formData.cognome}
                  onChange={(e) => handleChange("cognome", e.target.value)}
                  placeholder="Cognome"
                  required
                  disabled={isLoading}
                  className={`glass-effect border-0 shadow-sm ${isMobile ? 'text-base' : ''}`}
                  style={isMobile ? { minHeight: '44px' } : {}}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefono" className="text-sm font-medium text-slate-700">
                  Telefono
                </Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => handleChange("telefono", e.target.value)}
                  placeholder="es. 3331234567"
                  disabled={isLoading}
                  className={`glass-effect border-0 shadow-sm ${isMobile ? 'text-base' : ''}`}
                  style={isMobile ? { minHeight: '44px' } : {}}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="email@esempio.com"
                  disabled={isLoading}
                  className={`glass-effect border-0 shadow-sm ${isMobile ? 'text-base' : ''}`}
                  style={isMobile ? { minHeight: '44px' } : {}}
                />
              </div>
            </div>
          </div>

          {/* Billing Information Section */}
          <div className="border-t border-slate-200 pt-6">
            <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              <span>Informazioni di Fatturazione</span>
            </h3>
            <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>
              {/* Pricing Model Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Tipo di Fatturazione *
                </Label>
                
                {isMobile ? (
                  <NativeSelect
                    value={formData.pricing_model}
                    onChange={handlePricingModelChange}
                    options={pricingOptions}
                    placeholder="Seleziona tipo fatturazione"
                    disabled={isLoading}
                  />
                ) : (
                  <Select
                    value={formData.pricing_model}
                    onValueChange={handlePricingModelChange}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="glass-effect border-0 shadow-sm">
                      <SelectValue placeholder="Seleziona tipo fatturazione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_cost">Nessun costo</SelectItem>
                      <SelectItem value="per_day">Giornaliero per auto</SelectItem>
                      <SelectItem value="monthly_flat">Mensile fisso</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Rate Input Field */}
              {shouldShowRateField && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    {getRateLabel()}
                  </Label>
                  <div className="relative">
                    <Input
                      value={getCurrentRateValue()}
                      onChange={(e) => handleRateChange(e.target.value)}
                      placeholder={getRatePlaceholder()}
                      required={shouldShowRateField}
                      disabled={isLoading}
                      className={`glass-effect border-0 shadow-sm pr-12 ${isMobile ? 'text-base' : ''}`}
                      style={isMobile ? { minHeight: '44px' } : {}}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-slate-500 text-sm">€</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    {formData.pricing_model === 'per_day' 
                      ? "Tariffa applicata per ogni auto per ogni giorno di parcheggio" 
                      : "Tariffa fissa mensile indipendentemente dal numero di auto"
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Pricing Model Info */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                {formData.pricing_model === 'no_cost' && (
                  <span><strong>Nessun costo:</strong> Il cliente non verrà fatturato per il parcheggio.</span>
                )}
                {formData.pricing_model === 'per_day' && (
                  <span><strong>Giornaliero per auto:</strong> Il cliente pagherà la tariffa giornaliera moltiplicata per il numero di giorni di parcheggio per ogni auto.</span>
                )}
                {formData.pricing_model === 'monthly_flat' && (
                  <span><strong>Mensile fisso:</strong> Il cliente pagherà una tariffa fissa mensile indipendentemente dal numero di auto parcheggiate.</span>
                )}
              </p>
            </div>
          </div>

          <div className={`flex ${isMobile ? 'flex-col gap-3' : 'justify-end gap-3'} pt-4`}>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
              className={`px-6 ${isMobile ? 'order-2' : ''}`}
              style={isMobile ? { touchAction: 'manipulation' } : {}}
            >
              <X className="w-4 h-4 mr-2" />
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className={`bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 ${isMobile ? 'order-1' : ''}`}
              style={isMobile ? { touchAction: 'manipulation' } : {}}
            >
              <Save className="w-4 h-4 mr-2" />
              {isLoading ? 'Salvando...' : 'Salva'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}